import { isAxiosError } from 'axios'
import { createStore } from 'zustand'
import { subscribeWithSelector } from 'zustand/middleware'

import { captureAxiosError, logSentry } from '@gto/sentry'
import { sessionStore } from '@gto/session'

import { customInstance, isAuthRefreshFailedError } from '@/api/client'
import type { AffiliatePartnerProfileSchemaTermsAcceptedAt } from '@/api/gen/models/affiliate-partner-profile-schema-terms-accepted-at'
import type { Impersonation } from '@/api/impersonation-storage'
import { readImpersonation, writeImpersonation } from '@/api/impersonation-storage'

export type PartnerProfile = {
  id: string
  display_name: string
  email: string
  country: string
  status: string
  role: string
  notes: string
  tier: {
    name: string
    commission_rate: string
  } | null
  payout_method: string | null
  payout_details: Record<string, string> | null
  has_payout_details: boolean
  can_access_portal: boolean
  // Reuse the generated wire type (single source of truth) rather than re-declare
  // a looser hand-rolled `string | null`. The field is optional + nullable on the
  // contract — absent until the partner accepts, then an ISO datetime.
  terms_accepted_at?: AffiliatePartnerProfileSchemaTermsAcceptedAt
  created_at: string
}

/**
 * Outcome of a partner refetch. Callers must distinguish a genuinely expired
 * session (`unauthenticated`) from a transient transport failure (`error`): the
 * former should send the visitor to `/login`, the latter is safe to retry.
 * Collapsing both into a bare `isAuthorized: false` (the old behaviour) loses
 * that distinction and produces a wrong message when the session expires
 * mid-flow.
 *
 * The expiry signal is `AuthRefreshFailedError`, NOT a raw 401: every `/me/`
 * call goes through the response interceptor in `@/api/client`, which catches
 * the 401, attempts a DOAT refresh, and on failure rejects with that typed
 * error (the original `AxiosError` never reaches this catch).
 */
export type RefetchPartnerResult =
  | { status: 'success' }
  | { status: 'unauthenticated' }
  | { status: 'error' }

export type Store = {
  partner: PartnerProfile | null
  isAuthorized: boolean
  isInitialized: boolean
  /**
   * Admin impersonation target. When set, every API call attaches the
   * `X-Impersonate-Partner-Id` header (via the axios request interceptor)
   * and `/v4/affiliates/me/*` resolves to this partner on the backend, so
   * `partner` reflects the impersonated profile while impersonation is
   * active. The banner reads THIS — not `partner` — to decide whether to
   * render: `partner.role !== 'ADMIN'` is true for any non-admin partner
   * the admin happens to look at, so it cannot stand in for "I am admin
   * AND I am currently impersonating".
   */
  impersonation: Impersonation | null

  onLogin: (accessToken: string, refreshToken: string, cb?: () => void) => Promise<void>
  onLogout: (withRedirect?: boolean) => void
  initialize: () => Promise<void>
  refetchPartner: () => Promise<RefetchPartnerResult>
  setImpersonation: (impersonation: Impersonation | null) => void
}

export const store = createStore<Store>()(
  subscribeWithSelector((set, get) => ({
    partner: null,
    isAuthorized: false,
    isInitialized: false,
    // Seed from sessionStorage so a page reload mid-impersonation rehydrates
    // the banner + the X-Impersonate-Partner-Id header in lockstep — without
    // it the interceptor would see an active impersonation key while the
    // store thought it was idle, hiding the banner from a still-impersonating
    // admin. Safe today: affiliates-web is a Vite SPA and the leaf
    // `readImpersonation()` guards `typeof sessionStorage === 'undefined'`.
    // SSR caveat: this seed runs once at module import; any future RSC / SSR
    // migration MUST move the seed into an effect (top-level provider) or
    // into `initialize()` — running it during render would either crash or
    // produce a server snapshot disagreeing with the client's first paint.
    impersonation: readImpersonation(),

    setImpersonation: (impersonation) => {
      writeImpersonation(impersonation)
      set({ impersonation })
    },

    onLogin: async (accessToken, refreshToken, cb) => {
      sessionStore.getState().setAccessToken(accessToken)
      localStorage.setItem('user_refresh', refreshToken)
      // A fresh login must NOT inherit a prior session's impersonation target.
      // Session expiry can drop auth without routing through onLogout (which
      // clears it), so a stale impersonation key may still sit in sessionStorage
      // here; clear it BEFORE refetchPartner so /me/ resolves as the real
      // re-authenticated account, not the previously-impersonated partner.
      // (Reload of a live session goes through initialize(), which intentionally
      // keeps impersonation so a mid-impersonation refresh survives.)
      // Use the setImpersonation action — the single writer that keeps
      // sessionStorage and store state in sync — rather than two inline writes.
      get().setImpersonation(null)
      await get().refetchPartner()
      cb?.()
    },

    onLogout: (withRedirect = true) => {
      sessionStore.getState().clearAccessToken()
      localStorage.removeItem('user_refresh')
      // Clear impersonation on logout — otherwise the next admin (or the same
      // admin re-logging in) inherits the previous impersonation target.
      writeImpersonation(null)
      set({ partner: null, isAuthorized: false, impersonation: null })
      if (withRedirect) {
        window.location.href = '/login'
      }
    },

    initialize: async () => {
      const token = sessionStore.getState().accessToken
      const refreshToken = localStorage.getItem('user_refresh')

      if (token === null && refreshToken === null) {
        set({ isInitialized: true })

        return
      }

      // refetchPartner reports its own failures to Sentry and never throws — it
      // already cleared partner/isAuthorized on a non-success outcome, so there
      // is nothing extra to do here beyond marking initialization complete.
      await get().refetchPartner()
      set({ isInitialized: true })
    },

    refetchPartner: async () => {
      try {
        const profile = await customInstance<PartnerProfile>({
          url: '/v4/affiliates/me/',
          method: 'GET',
        })
        set({ partner: profile, isAuthorized: true, isInitialized: true })

        return { status: 'success' }
      } catch (error) {
        const isUnauthenticated = isAuthRefreshFailedError(error)
        if (isAxiosError(error)) {
          captureAxiosError(error, 'react')
        } else {
          logSentry('Failed to fetch partner profile: ', error)
        }
        set({
          partner: null,
          isAuthorized: false,
        })

        return { status: isUnauthenticated ? 'unauthenticated' : 'error' }
      }
    },
  })),
)
